/* eslint-disable prettier/prettier */
import React, { createContext, useCallback, useState, useContext } from 'react';
import { IFormBusca } from '../pages/Procedimento';
import api from '../services/api';

import { formatPrice } from '../utils/funcoes';
import { IPerfil } from './perfil';
import { IOption } from '../components/SelectSync';
import { useToast } from './toast';
import getResponseErrors from '../utils/getResponseErrors';

interface IPaginacao {
  termoBusca: IFormBusca;
  limite: number;
  pagina: number;
}

export interface IValorProcedimento {
  ProCodigo: number;
  ConvCodigo: number;
  valor: number;
  ProRetorno: number;
}

export interface IValoresProcedimento extends IValorProcedimento {
  convenio?: string;
  convenioProcedimento?: {
    ConvCodigo: number;
    ConvNome: string;
    ConvAtivo: boolean;
  };

  valorFormatado: string;
}

export interface IProcedimento {
  ProCodigo: number;
  ProPerCodigo?: number;
  ProNome: string;
  ProCodigoAlvaro: string;
  ProCor: string;
  ProIcone: string;
  ProValor: number;
  ProDuracao: number;
  ProRetorno: number;
  ProLimite: number;
  ProValorPlano: number;
  ProCarencia: number;
  ProValorPlano2: number;
  ProAtivo: boolean;
  ProFavorito: boolean;
  ProValorCobrado: number;
  perfil?: IPerfil;
  valoresProcedimentoConvenio?: IValoresProcedimento[];
  valorFormatado?: string;
  valorCartao?: string;
  valorCarencia?: string;
  ativoFormatado?: string;
  ProOnline?: boolean;
  onlineFormatado?: string;
  ProTelemedicina?: boolean;
  telemedicinaFormatado?: string;
}

interface ProcedimentoContextData {
  loading: boolean;
  procedimentos: IProcedimento[];
  totalRegistros?: number;
  buscaProcedimentos(paginacao: IPaginacao): Promise<void>;
  addProcedimento(
    dadosProcedimento: IProcedimento,
    perf?: IOption,
    proCodigo?: number,
  ): Promise<boolean>;
  addValorConvenio(
    dadosValorProcedimento: IValorProcedimento,
    convenio: string,
  ): Promise<void>;
  deleteProcedimento(codigo: number): Promise<void>;
  deleteValorConvenio(ProCodigo: number, ConvCodigo: number): Promise<void>;
}

const ProcedimentoContext = createContext<ProcedimentoContextData>(
  {} as ProcedimentoContextData,
);

const ProcedimentoProvider: React.FC = ({ children }) => {
  const [loading, setLoading] = useState(false);

  const { addToast } = useToast();
  const [procedimentos, setProcedimentos] = useState<IProcedimento[]>([]);
  const [totalRegistros, setTotalRegistros] = useState<number | undefined>(
    undefined,
  );

  const buscaProcedimentos = useCallback(
    async (paginacao: IPaginacao) => {
      setLoading(true);
      try {
        const { nome, perfil, situacao, online, telemedicina } = paginacao.termoBusca;
        let query = '';
        if (nome !== '') query = `&ProNome=${nome}`;
        if (perfil !== '') query = `${query}&ProPerCodigo=${perfil}`;
        if (situacao !== '') query = `${query}&ProAtivo=${situacao}`;
        if (online !== '' && online !== undefined) query = `${query}&ProOnline=${online}`;
        if (telemedicina !== '' && telemedicina !== undefined) query = `${query}&ProTelemedicina=${telemedicina}`;

        const response = await api.get(
          `/procedimentos?pagina=${paginacao.pagina}&limite=${paginacao.limite}${query}`,
        );

        const procedimentosApi: IProcedimento[] = response.data[0];

        const prodedimentosFormatado = procedimentosApi.map(proc => {
          return {
            ...proc,
            valorFormatado: formatPrice(proc.ProValor),
            valorCartao: formatPrice(proc.ProValorPlano),
            valorCarencia: formatPrice(proc.ProValorPlano2),
            ativoFormatado: proc.ProAtivo ? 'Sim' : 'Não',
            onlineFormatado: proc.ProOnline ? 'Sim' : 'Não',
            telemedicinaFormatado: proc.ProTelemedicina ? 'Sim' : 'Não',
            valoresProcedimentoConvenio: proc.valoresProcedimentoConvenio?.map(
              conv => {
                return {
                  ...conv,
                  convenio: conv.convenioProcedimento?.ConvNome,
                  valorFormatado: formatPrice(conv.valor),
                };
              },
            ),
          };
        });

        setTotalRegistros(
          response.data[1] === 0 ? undefined : response.data[1],
        );

        setProcedimentos(prodedimentosFormatado);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar procedimentos',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const deleteProcedimento = useCallback(
    async (codigo: number) => {
      setLoading(true);
      try {
        await api.delete(`/procedimentos/${codigo}`);
        setProcedimentos(procedimentosState =>
          procedimentosState?.filter(pro => pro.ProCodigo !== codigo),
        );
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível deletar procedimento',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const addProcedimento = useCallback(
    async (
      dadosProcedimento: IProcedimento,
      perf: IOption | undefined,
      proCodigo: number | undefined,
    ) => {
      let response;
      setLoading(true);
      let retorno = false;
      try {
        if (proCodigo) {
          response = await api.put<IProcedimento>(
            `/procedimentos/${proCodigo}`,
            dadosProcedimento,
          );
        } else {
          response = await api.post<IProcedimento>(
            '/procedimentos',
            dadosProcedimento,
          );
        }

        const retornoProcedimento: IProcedimento = {
          ...response.data,
          valorFormatado: formatPrice(dadosProcedimento.ProValor),
          valorCartao: formatPrice(dadosProcedimento.ProValorPlano),
          valorCarencia: formatPrice(dadosProcedimento.ProValorPlano2),
          ativoFormatado: dadosProcedimento.ProAtivo ? 'Sim' : 'Não',
          onlineFormatado: dadosProcedimento.ProOnline ? 'Sim' : 'Não',
          telemedicinaFormatado: dadosProcedimento.ProTelemedicina ? 'Sim' : 'Não',
          ProPerCodigo: Number(perf?.value),
          perfil: {
            PerCodigo: Number(perf?.value),
            PerNome: perf?.label || '',
            PerAtivo: true,
            PerTipo: 1,
          },
        };

        setProcedimentos(state => {
          return state
            ? [retornoProcedimento].concat(
              state.filter(
                p => p.ProCodigo !== retornoProcedimento.ProCodigo,
              ),
            )
            : [retornoProcedimento];
        });
        retorno = true;
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível adicionar procedimento',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
      return retorno;
    },
    [addToast],
  );

  const addValorConvenio = useCallback(
    async (dadosValorProcedimento: IValorProcedimento, convenio: string) => {
      setLoading(true);
      try {
        const response = await api.post(
          `procedimentos/valor`,
          dadosValorProcedimento,
        );

        const retornoValor = {
          ...response.data,
          valorFormatado: formatPrice(response.data.valor),
          convenio,
        };

        setProcedimentos(state => {
          const proc = state.find(
            pr => pr.ProCodigo === dadosValorProcedimento.ProCodigo,
          );
          if (!proc) return state;

          const procFormatado: IValoresProcedimento[] = proc.valoresProcedimentoConvenio
            ? [retornoValor].concat(
              proc.valoresProcedimentoConvenio.filter(
                valor =>
                  valor.ConvCodigo !== dadosValorProcedimento.ConvCodigo &&
                  valor.ProCodigo !== dadosValorProcedimento.ProCodigo,
              ),
            )
            : [retornoValor];

          const retornoProcedimento: IProcedimento = {
            ...proc,
            valoresProcedimentoConvenio: procFormatado,
          };

          return state
            ? [retornoProcedimento].concat(
              state.filter(p => p.ProCodigo !== proc.ProCodigo),
            )
            : [];
        });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível adicionar procedimento',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const deleteValorConvenio = useCallback(
    async (ProCodigo: number, ConvCodigo: number) => {
      setLoading(true);
      try {
        await api.delete(
          `procedimentos/valor/${ProCodigo}?ConvCodigo=${ConvCodigo}`,
        );

        setProcedimentos(state => {
          const proc = state.find(pr => pr.ProCodigo === ProCodigo);
          if (!proc) return state;
          const procFormatado: IValoresProcedimento[] = proc.valoresProcedimentoConvenio
            ? proc.valoresProcedimentoConvenio.filter(
              pro => pro.ConvCodigo !== ConvCodigo,
            )
            : [];

          const retornoProcedimento: IProcedimento = {
            ...proc,
            valoresProcedimentoConvenio: procFormatado,
          };
          console.log(retornoProcedimento);
          return state
            ? [retornoProcedimento].concat(
              state.filter(p => p.ProCodigo !== proc.ProCodigo),
            )
            : [];
        });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível remover valor',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );
  return (
    <ProcedimentoContext.Provider
      value={{
        loading,
        procedimentos,
        totalRegistros,
        buscaProcedimentos,
        addProcedimento,
        deleteProcedimento,
        addValorConvenio,
        deleteValorConvenio,
      }}
    >
      {children}
    </ProcedimentoContext.Provider>
  );
};

function useProcedimento(): ProcedimentoContextData {
  const context = useContext(ProcedimentoContext);

  if (!context) {
    throw new Error(
      'useProcedimento deve ser usado com o Procedimento Provider',
    );
  }

  return context;
}

export { ProcedimentoProvider, useProcedimento };
